import { toWSDateString, WSText } from "@wingspanhq/fe-component-library";
import { Invoice } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesPersons } from "@query";
import { useSession } from "@hooks/useSession";
import { Card } from "../../modules/PaymentApp/components/Card/Card";
import { convertDateToEndDay, isSameDateSafe } from "../../utils/dates";
import { selectorIsInvoicePaidOffPlatform } from "../selectors/selectorIsInvoicePaidOffPlatform";
import { Activity, ActivityItem } from "./Activity";

type Props = {
  invoice: Invoice;
  /**
   * The client's contact address, resolved by the screen (see
   * [[selectorCounterpartyEmail]]) rather than derived here, so every line below
   * names the same recipient the header does.
   */
  payerEmail?: string;
};

export const InvoiceActivity: React.FC<Props> = ({ invoice, payerEmail }) => {
  const { personId } = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery(
    { personId },
    { enabled: !!personId }
  );

  const events = invoice.events;
  const isPaidOffPlatform = selectorIsInvoicePaidOffPlatform(invoice);

  const activity = useMemo(() => {
    const activity: ActivityItem[] = [];

    // Every line below names one of the two parties. The email is the name when
    // it resolves; when it does not — both are optional on v3, and the person
    // read may not have landed — the party is named generically rather than
    // interpolated as the literal string `undefined`.
    const payerLabel = payerEmail || "your client";
    const payeeLabel = queryPerson.data?.email || "you";

    if (events?.createdAt) {
      activity.push({
        title: "Invoice was created",
        description: toWSDateString(events.createdAt),
        date: events.createdAt
      });
    }

    if (events?.openedAt) {
      activity.push({
        title: `Sent to ${payerLabel}`,
        description: toWSDateString(events.openedAt),
        date: events.openedAt
      });
    }

    if (events?.emailReceivedAt && events.emailReceivedAt.length > 0) {
      activity.push({
        title: `Received by ${payerLabel}`,
        description: toWSDateString(events.emailReceivedAt[0]),
        date: events.emailReceivedAt[0]
      });
    }

    // Work with viewed at only if it is before paidAt and depositedAt
    // Viewed event should never come after paid and deposited
    const allowedViewedAt = (events?.emailViewedAt || []).filter(date =>
      [events?.paidAt, events?.depositedAt, events?.instantPayoutAt].every(d =>
        d ? d > date : true
      )
    );

    if (allowedViewedAt.length > 0) {
      allowedViewedAt.sort((a, b) => b.getTime() - a.getTime());

      const firstViewedAt = allowedViewedAt[allowedViewedAt.length - 1];

      if (firstViewedAt) {
        activity.push({
          title: `Viewed by ${payerLabel}`,
          description: toWSDateString(firstViewedAt),
          date: firstViewedAt
        });
      }

      const secondViewedAt = firstViewedAt
        ? allowedViewedAt.find(
            date => date.getTime() - firstViewedAt.getTime() > 60 * 60 * 1000
          )
        : undefined;

      if (secondViewedAt) {
        activity.push({
          title: `Viewed by ${payerLabel}`,
          description: toWSDateString(secondViewedAt),
          date: secondViewedAt
        });
      }
    }

    if (events?.approvedAt) {
      activity.push({
        title: `Approved by ${payerLabel}`,
        description: toWSDateString(events.approvedAt),
        date: events.approvedAt
      });
    }

    if (events?.unapprovedAt) {
      activity.push({
        title: `Unapproved by ${payerLabel}`,
        description: toWSDateString(events.unapprovedAt),
        date: events.unapprovedAt
      });
    }

    if (events?.preApprovedAt) {
      activity.push({
        title: `Pre-approved by ${payerLabel}`,
        description: toWSDateString(events.preApprovedAt),
        date: events.preApprovedAt
      });
    }

    if (
      events?.sentDueTodayAt &&
      !isSameDateSafe(events.openedAt, events.sentDueTodayAt)
    ) {
      activity.push({
        title: `Reminder sent to ${payerLabel}`,
        description: toWSDateString(events.sentDueTodayAt),
        date: events.sentDueTodayAt
      });
    }

    if (
      events?.sentPaymentRequestedToClient &&
      !isSameDateSafe(events.openedAt, events.sentPaymentRequestedToClient)
    ) {
      activity.push({
        title: `Reminder sent to ${payerLabel}`,
        description: toWSDateString(events.sentPaymentRequestedToClient),
        date: events.sentPaymentRequestedToClient
      });
    }

    if (events?.sentDueIn3DaysAt) {
      activity.push({
        title: `Reminder sent to ${payerLabel}`,
        description: toWSDateString(events.sentDueIn3DaysAt),
        date: events.sentDueIn3DaysAt
      });
    }

    if (events?.sentDue3DaysAgoAt) {
      activity.push({
        title: `Reminder sent to ${payerLabel}`,
        description: toWSDateString(events.sentDue3DaysAgoAt),
        date: events.sentDue3DaysAgoAt
      });
    }

    if (events?.sentDue7DaysAgoAt) {
      activity.push({
        title: `Reminder sent to ${payerLabel}`,
        description: toWSDateString(events.sentDue7DaysAgoAt),
        date: events.sentDue7DaysAgoAt
      });
    }

    if (events?.paymentInTransitAt) {
      activity.push({
        title: `Payment in transit on ${toWSDateString(
          events.paymentInTransitAt
        )}`,
        description: "Waiting on client's wire transfer to arrive",
        date: events.paymentInTransitAt
      });
    }

    if (events?.paidAt) {
      activity.push(
        events.instantPayoutAt
          ? {
              title: "Invoice paid",
              description: toWSDateString(events.paidAt),
              date: events.paidAt
            }
          : {
              title: `Paid on ${toWSDateString(events.paidAt)}`,
              description: isPaidOffPlatform
                ? "Invoice was paid outside of Wingspan"
                : events.estimatedDepositAt
                  ? `Payout initiated on ${toWSDateString(
                      events.estimatedDepositAt,
                      "monthDayYear"
                    )}`
                  : "Invoice was paid through Wingspan",
              date: events.paidAt
            }
      );
    }

    if (events?.depositedAt && !events.instantPayoutAt) {
      activity.push({
        title: "Payout initiated",
        description: `Initiated on ${toWSDateString(
          events.depositedAt,
          "monthDate"
        )}. Funds may take 1–2 business days to arrive.`,
        date: convertDateToEndDay(events.depositedAt)
      });
    }

    if (events?.instantPayoutAt) {
      activity.push({
        title: "Deposited instantly",
        description: toWSDateString(events.instantPayoutAt),
        date: events.instantPayoutAt
      });
    }

    if (events?.memberDisputedAt) {
      activity.push({
        title: `Invoice disputed`,
        description: toWSDateString(events.memberDisputedAt),
        date: events.memberDisputedAt
      });
    }

    if (events?.clientResolvedDisputeAt) {
      activity.push({
        title: "Dispute resolved",
        description: `Client resolved dispute on ${toWSDateString(
          events.clientResolvedDisputeAt,
          "monthDate"
        )}`,
        date: events.clientResolvedDisputeAt
      });
    }

    if (events?.memberAcceptedAt) {
      activity.push({
        title: "Invoice accepted by contractor",
        description: toWSDateString(events.memberAcceptedAt),
        date: events.memberAcceptedAt
      });
    }

    if (events?.clientDeclinedAt) {
      activity.push({
        title: `Rejected by ${payerLabel}`,
        description: toWSDateString(events.clientDeclinedAt),
        date: events.clientDeclinedAt
      });
    }

    if (events?.memberResubmittedAt) {
      activity.push({
        title: `Resubmitted by ${payeeLabel}`,
        description: `You resubmitted at ${toWSDateString(
          events.memberResubmittedAt
        )}`,
        date: events.memberResubmittedAt
      });
    }

    activity.sort((a, b) => {
      if (a.date > b.date) return -1;
      if (a.date < b.date) return 1;
      return 0;
    });

    return activity;
  }, [events, isPaidOffPlatform, payerEmail, queryPerson.data?.email]);

  return (
    <Card>
      <WSText.Heading5 mb="XL">All activity</WSText.Heading5>
      <Activity activity={activity} />
    </Card>
  );
};
